import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Bed, Plus, Pencil, Trash2, Users, DollarSign, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export const RoomManager = ({ hotelId }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [isNewRoom, setIsNewRoom] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_per_night: '',
    weekday_rate: '',
    weekend_rate: '',
    max_guests: '',
    image_url: ''
  });

  const backendUrl = BACKEND_URL;

  useEffect(() => {
    loadRooms();
  }, [hotelId]);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/hotels/${hotelId}/rooms`);
      const data = await response.json();
      setRooms(data.rooms || []);
    } catch (error) {
      console.error('Failed to load rooms:', error);
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({
      name: '',
      description: '',
      price_per_night: '',
      weekday_rate: '',
      weekend_rate: '',
      max_guests: '2',
      image_url: ''
    });
    setEditingRoom(null);
    setIsNewRoom(true);
    setShowDialog(true);
  };

  const handleEdit = (room) => {
    setFormData({
      name: room.name || '',
      description: room.description || '',
      price_per_night: room.price_per_night?.toString() || '',
      weekday_rate: room.weekday_rate?.toString() || '',
      weekend_rate: room.weekend_rate?.toString() || '',
      max_guests: room.max_guests?.toString() || '2',
      image_url: room.image_url || ''
    });
    setEditingRoom(room);
    setIsNewRoom(false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price_per_night) {
      toast.error('Room name and price are required');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price_per_night: parseFloat(formData.price_per_night),
        weekday_rate: formData.weekday_rate ? parseFloat(formData.weekday_rate) : null,
        weekend_rate: formData.weekend_rate ? parseFloat(formData.weekend_rate) : null,
        max_guests: parseInt(formData.max_guests) || 2,
        image_url: formData.image_url || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'
      };

      let response;
      if (isNewRoom) {
        response = await fetch(`${backendUrl}/api/hotels/${hotelId}/rooms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${backendUrl}/api/rooms/${editingRoom.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) throw new Error('Save failed');
      
      toast.success(isNewRoom ? 'Room created successfully' : 'Room updated successfully');
      setShowDialog(false);
      loadRooms();
    } catch (error) {
      console.error('Failed to save room:', error);
      toast.error('Failed to save room');
    }
  };

  const handleDelete = async (roomId, roomName) => {
    if (!confirm(`Are you sure you want to delete "${roomName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/rooms/${roomId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
      
      toast.success('Room deleted successfully');
      loadRooms();
    } catch (error) {
      console.error('Failed to delete room:', error);
      toast.error(error.message || 'Failed to delete room');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading rooms...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="settings-room-manager-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Bed className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="heading-font">Room Management</CardTitle>
                <CardDescription className="mt-1">
                  Add, edit, or remove room types for your hotel
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleAddNew} size="sm" data-testid="room-manager-add-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {rooms.length === 0 ? (
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                No rooms added yet. Click "Add Room" to create your first room type.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {rooms.map((room) => (
                <div
                  key={room.id}
                  className="flex items-center justify-between p-4 border border-border rounded-xl hover:bg-accent/30 transition-colors"
                  data-testid={`room-card-${room.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{room.name}</h4>
                      {room.weekday_rate && room.weekend_rate ? (
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                            ${room.weekday_rate} Mon-Thu
                          </Badge>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                            ${room.weekend_rate} Fri-Sun
                          </Badge>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          ${room.price_per_night}/night
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {room.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Up to {room.max_guests} guests
                      </span>
                      {room.is_available ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Available
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600">
                          <AlertCircle className="w-3 h-3" />
                          Unavailable
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(room)}
                      data-testid={`room-edit-button-${room.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(room.id, room.name)}
                      data-testid={`room-delete-button-${room.id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="room-editor-dialog">
          <DialogHeader>
            <DialogTitle>{isNewRoom ? 'Add New Room' : 'Edit Room'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="room-name">Room Name *</Label>
              <Input
                id="room-name"
                data-testid="room-editor-name-input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Deluxe Ocean View"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="room-description">Description</Label>
              <Textarea
                id="room-description"
                data-testid="room-editor-description-input"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Spacious room with king bed and ocean views..."
                rows={3}
              />
            </div>

            <Alert className="bg-primary/5 border-primary/20">
              <DollarSign className="w-4 h-4 text-primary" />
              <AlertDescription>
                <strong>Dynamic Pricing:</strong> Set different rates for weekdays (Mon-Thu) and weekends (Fri-Sun) to maximize revenue!
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="room-weekday-rate">Weekday Rate (Mon-Thu)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="room-weekday-rate"
                    type="number"
                    data-testid="room-editor-weekday-rate-input"
                    value={formData.weekday_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, weekday_rate: e.target.value }))}
                    placeholder="100"
                    className="pl-9"
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="room-weekend-rate">Weekend Rate (Fri-Sun)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="room-weekend-rate"
                    type="number"
                    data-testid="room-editor-weekend-rate-input"
                    value={formData.weekend_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, weekend_rate: e.target.value }))}
                    placeholder="150"
                    className="pl-9"
                    min="0"
                    step="1"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="room-price">Legacy Price *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="room-price"
                    type="number"
                    data-testid="room-editor-price-input"
                    value={formData.price_per_night}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_per_night: e.target.value }))}
                    placeholder="150"
                    className="pl-9"
                    min="0"
                    step="1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Used only if weekday/weekend rates not set</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="room-guests">Max Guests</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="room-guests"
                    type="number"
                    data-testid="room-editor-guests-input"
                    value={formData.max_guests}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_guests: e.target.value }))}
                    placeholder="2"
                    className="pl-9"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="room-image">Image URL</Label>
              <Input
                id="room-image"
                data-testid="room-editor-image-input"
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://images.unsplash.com/..."
              />
              <p className="text-xs text-muted-foreground">Leave blank for default image</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="room-editor-save-button">
              <Save className="w-4 h-4 mr-2" />
              {isNewRoom ? 'Create Room' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

function Save(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
}
